import React from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import find from 'lodash/find';
import useIsMobile from '../../../../hooks/useIsMobile';
import { Spinner } from 'formlabs-web-components/Navigation';
import { Picture } from 'formlabs-web-components/Media';
import {
  Cell,
  Grid,
  GridContainer,
  Section
} from 'formlabs-web-components/Grid';
import { Header } from 'formlabs-web-components/Type';

import DetailPrice from '../DetailPrice';
import ProductOptionControl, {
  ProductOptionListControl
} from '../../ProductOptionControl';
import ProductOptionModal from '../../ProductOptionModal';
import ListDropdown from '../../../../components/navigation/ListDropdown';
import { Pill } from 'formlabs-web-components/Type';
import ContactSalesBlock from './ContactSalesBlock';

const copyStrings = t => ({
  'SLS 3D Printer': {
    title: t('SLS 3D Printer'),
    subtitle: t('Add more printers to print in parallel'),
    copy: t(
      'The Fuse 1 selective laser sintering (SLS) 3D printer delivers industrial power in a small footprint. Add more printers to maximize your throughput and run multiple jobs in tandem. Each Fuse 1 printer includes one build chamber.'
    )
  },
  Accessories: {
    title: t('Accessories'),
    subtitle: t('Accessories for an efficient, ergonomic workflow'),
    copy: t(
      'Add additional build chambers and powder cartridges, compatible with the Fuse 1 and the Fuse Sift, to enable continuous printing and reduce downtime. The steel stand raises the Fuse 1 to an ergonomic height, keeping your printer stable and level while enabling easy access.'
    )
  },
  Materials: {
    title: t('Materials'),
    subtitle: t('Print more with additional material'),
    copy: t(
      'Our nylon powders are specifically formulated for the Fuse 1. We recommend at least 12 kg to start printing, 24 kg to ramp up efficiently, and 60 kg for high throughput.'
    )
  },
  'Post-Processing': {
    title: t('Post-Processing'),
    subtitle: t('Safe, efficient powder recovery'),
    copy: t(
      'The Fuse Sift is an all-in-one station that combines part extraction, automatic powder recovery, storage, and mixing in a single free-standing device. An external vacuum, sold separately, is required and helps you safely maintain a clean workspace. The Fuse Sift is required for a complete SLS workflow. Additional Fuse Sift units can increase throughput by allowing multiple operators to post-process in parallel.'
    )
  }
});

const CustomizeStep = ({
  activeBundle,
  bundles,
  bundleOptions,
  bundleOptionsDispatcher,
  setActiveSku,
  storeId,
  cdnUrl,
  baseUrl,
  activeBundleData,
  activeBundleLoading
}) => {
  const [t] = useTranslation();
  const copy_strings = copyStrings(t);
  const groups = groupBy(
    get(activeBundle, 'bundle_product_options', []),
    'title'
  );
  const mobile = useIsMobile(900);

  return (
    <GridContainer>
      <Grid direction="x" gutters="margin">
        <Cell>
          <Section
            backgroundColor="light-grey"
            padding="2rem"
            paddingMedium="2rem"
            paddingSmall="2rem"
          >
            <div style={{ display: !mobile ? 'flex' : null }}>
              <span
                style={{
                  fontFamily:
                    'Proxima Nova, proxima-nova, Helvetica Neue, Helvetica',
                  fontStyle: 'normal',
                  fontWeight: 'bold',
                  fontSize: '30px',
                  lineHeight: '34px',
                  paddingRight: '5px'
                }}
              >
                {' '}
                {`${t('Customize Your')}`}
              </span>
              <ListDropdown
                dropdownType="textDropdown"
                title={activeBundle.name}
              >
                {bundles.map(bundle => {
                  return {
                    name: bundle.name,
                    handler: () => setActiveSku(bundle.sku)
                  };
                })}
              </ListDropdown>
            </div>
            <p>
              {t(
                'We’ve pre-selected the recommended items based on your previously selected production needs. Customize your package below.'
              )}
            </p>
          </Section>
          <Section
            marginBeforeSmall="1rem"
            marginBeforeMedium="1rem"
            marginBefore="1rem"
          >
            {Object.keys(groups).map(groupTitle => {
              const group = groups[groupTitle];
              const optionCopy = get(copy_strings, group[0].title);
              const quantityIndicator = group
                .map(p => {
                  const product = get(p, 'product_links[0]', null);
                  if (!product) return 0;
                  const currentBundleOption = find(
                    bundleOptions,
                    option => option['option_id'] === p['option_id']
                  );
                  const quantity = get(
                    get(currentBundleOption, 'product_links')
                      ? get(currentBundleOption, 'product_links')[0]
                      : [],
                    'selectedQty',
                    !!currentBundleOption ? 1 : 0,
                    0
                  );
                  return product.qty * quantity;
                })
                .reduce((a, b) => a + b, 0);

              if (group[0].title.includes('Service')) {
                return null;
              }
              return (
                <div
                  key={groupTitle}
                  style={{ borderTop: '1px solid #E6E6E6', paddingTop: '2rem' }}
                >
                  <Grid
                    direction={mobile ? 'y' : 'x'}
                    gutters="margin"
                    key={group[0].title}
                  >
                    <Cell mediumSize={12}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Header level={4} displayAs={4}>
                          {optionCopy && optionCopy.title}
                        </Header>
                        {quantityIndicator > 0 && (
                          <div style={{ marginLeft: '0.3rem' }}>
                            <Pill
                              color="blue"
                              text={`${quantityIndicator}`}
                              shape="circle"
                            />
                          </div>
                        )}
                      </div>
                      <div style={{ margin: '2rem 0' }}>
                        <ProductOptionListControl>
                          {group.map(p => {
                            const product = get(p, 'product_links[0]', null);
                            if (!product) return;
                            const displayQty = product.qty;
                            const currentBundleOption = find(
                              bundleOptions,
                              option => option['option_id'] === p['option_id']
                            );
                            const isActive = !!currentBundleOption;
                            const optionData = find(
                              activeBundleData,
                              option => {
                                return option.sku === product.sku;
                              }
                            );
                            const imagePath = get(
                              optionData,
                              'attributes.image',
                              null
                            );
                            return (
                              <ProductOptionControl
                                key={p['option_id']}
                                controlPosition="center"
                                imagePosition="left" // TODO maybe we have to use ProductImage component to fetch the item images
                                imageElement={
                                  !activeBundleLoading ? (
                                    imagePath ? (
                                      <div>
                                        <Picture
                                          largeImage={`${cdnUrl}${imagePath}`}
                                        />
                                      </div>
                                    ) : null
                                  ) : (
                                    <Spinner size={3} color="#0762c8" />
                                  )
                                }
                                name={p['option_id']}
                                value={product.id}
                                title={
                                  displayQty > 1
                                    ? `${displayQty} x ${product.name}`
                                    : product.name
                                }
                                isActive={isActive}
                                hatTitle={
                                  product.recommended && t('Recommended')
                                }
                                onChange={e => {
                                  const element = e.target;

                                  if (element.checked) {
                                    const data = {
                                      ...p,
                                      product_links: [
                                        { ...product, selectedQty: 1 }
                                      ]
                                    };
                                    bundleOptionsDispatcher({
                                      type: 'add',
                                      data,
                                      id: product.id,
                                      optionId: p['option_id']
                                    });
                                  } else {
                                    if (!p.required) {
                                      bundleOptionsDispatcher({
                                        type: 'remove',
                                        id: product.id,
                                        optionId: p['option_id']
                                      });
                                    }
                                  }
                                }}
                                type="checkbox"
                                quantityOptions={{
                                  disabled:
                                    product['can_change_quantity'] === 0 ||
                                    !isActive,
                                  quantity: get(
                                    currentBundleOption,
                                    'selectedQty',
                                    isActive ? 1 : 0
                                  ),
                                  minQuantity: 1,
                                  onQuantityChange: qty => {
                                    if (isActive) {
                                      bundleOptionsDispatcher({
                                        type: 'setQty',
                                        id: product.id,
                                        optionId: p['option_id'],
                                        qty
                                      });
                                    }
                                  }
                                }}
                              >
                                <DetailPrice
                                  isRequired={p.required}
                                  price={product.price}
                                  qty={product.qty}
                                  storeId={storeId}
                                  optionData={optionData}
                                />
                                {!product.sku.includes('.sp') && optionData ? (
                                  <ProductOptionModal
                                    title={product.name}
                                    price={product.price}
                                    link={`${baseUrl}${get(
                                      optionData,
                                      'url',
                                      ''
                                    )}`}
                                    storeId={storeId}
                                    description={get(
                                      optionData,
                                      'attributes.description',
                                      null
                                    )}
                                    images={get(
                                      optionData,
                                      'media_gallery_entries',
                                      null
                                    )}
                                    cdnUrl={cdnUrl}
                                    loading={optionData.length}
                                  />
                                ) : null}
                              </ProductOptionControl>
                            );
                          })}
                        </ProductOptionListControl>
                      </div>
                    </Cell>
                    <Cell mediumSize={8} mediumOffset={2} largeOffset={2}>
                      {optionCopy && (
                        <>
                          <Header level={5} displayAs={5} weight={700}>
                            {optionCopy.subtitle}
                          </Header>
                          <p>{optionCopy.copy}</p>
                        </>
                      )}
                    </Cell>
                  </Grid>
                </div>
              );
            })}
            <Grid direction={mobile ? 'y' : 'x'}>
              <Cell mediumSize={12}>
                <ContactSalesBlock />
              </Cell>
            </Grid>
          </Section>
        </Cell>
      </Grid>
    </GridContainer>
  );
};

export default CustomizeStep;
